import React, {useEffect, useMemo, useState} from 'react';
import {Select, Switch} from 'antd';
import {Direction, ProtectionPlusOptions, ReportName, SeverityType} from 'src/utils/enums';
import {FilterKey, FilterKeyDisplayNames, FilterOptions, reportFilters} from '@src/dtos/reports.dto';
import {useStores} from '@src/components/store';

export type ReportFilterState = {
    direction?: Direction,
    action?: ProtectionPlusOptions,
    severity?: SeverityType | '',
    showUSA?: boolean
}

export const useReportFilters = (reportType: ReportName, routeState?: { direction?: Direction }) => {

    const [filters, setFilters] = useState<ReportFilterState | undefined>(undefined);
    const [filtersReportName, setFiltersReportName] = useState<ReportName>(reportType);
    const { userPreferencesStore } = useStores();

    const applicableFilters = useMemo(() => reportFilters[reportType] || []
        , [reportType]);

    // Reset filters when reportType or initial direction changes
    useEffect(() => {
        // Mark filters as belonging to the current reportType before triggering downstream effects.
        setFiltersReportName(reportType);

        if (!applicableFilters.length) {
            setFilters(undefined);
            return;
        }

        setFilters({
            [FilterKey.direction]: applicableFilters.includes(FilterKey.direction)
                ? (routeState?.direction ?? Direction.Inbound)
                : undefined,
            [FilterKey.action]: applicableFilters.includes(FilterKey.action)
                ? ProtectionPlusOptions.BLOCK
                : undefined,
            [FilterKey.severity]: applicableFilters.includes(FilterKey.severity)
                ? ''
                : undefined,
            [FilterKey.showUSA]: applicableFilters.includes(FilterKey.showUSA)
                ? userPreferencesStore.reportsShowUSA
                : undefined
        });
    }, [applicableFilters, reportType, routeState?.direction]);

    const handleChange = (filterKey: FilterKey, value: any) => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [filterKey]: value
        }));
    };

    const FilterControls = useMemo(() => (
        <div className={'report-header-search-and-filters'}>
            <div className={'report-header-filters'}>
                {applicableFilters.map(filterKey => <div className="filter-selection-wrapper" key={filterKey}>
                    <div className="filter-title">{FilterKeyDisplayNames[filterKey]}</div>
                    {filterKey === FilterKey.showUSA ? (
                        <Switch
                            className="show-usa-toggle"
                            checked={filters?.[filterKey]}
                            onChange={isShowUSA => {
                                userPreferencesStore.updateShowUSA(isShowUSA);
                                handleChange(filterKey, isShowUSA);
                            }}
                        />
                    ) : (
                        <Select
                            className="generic-select"
                            value={filters?.[filterKey]}
                            onChange={value => handleChange(filterKey, value)}
                            options={FilterOptions[filterKey]}
                        />
                    )}
                    </div>
                )}
            </div>
        </div>
    ), [applicableFilters, filters]);

    return {filters, filtersReportName, FilterControls};
};

export default useReportFilters;
